import type { MouseEventHandler } from 'react';
import { useCallback } from 'react';
import { _notNil } from '@/littledash';
import type { DataTableCellIndexCoordinate } from '@/components/UI/DataTableV2/DataTable.model';
import { useDataTable } from '../DataTableProvider';

export interface UseCellMouseHandlersOptions {
  disabled?: boolean;
  onDoubleClick?: () => void;
}

export interface UseCellMouseHandlersReturn {
  handleMouseDown: MouseEventHandler<HTMLElement>;
  handleMouseEnter: MouseEventHandler<HTMLElement>;
  handleMouseUp: MouseEventHandler<HTMLElement>;
  handleDoubleClick: MouseEventHandler<HTMLElement>;
}

/**
 * Standardized mouse event handlers for cell selection
 *
 * This hook provides consistent mouse interaction behavior across all cell types,
 * including click-to-select, drag-to-select, and double-click to activate.
 *
 * @param coordinate - Cell index coordinate
 * @param options - Optional configuration (disabled, custom double-click handler)
 * @returns Object containing mouse event handlers
 */
export const useCellMouseHandlers = (
  coordinate: DataTableCellIndexCoordinate,
  options: UseCellMouseHandlersOptions = {}
): UseCellMouseHandlersReturn => {
  const { disabled = false, onDoubleClick } = options;
  const { dataTableService } = useDataTable();

  const handleMouseDown: MouseEventHandler<HTMLElement> = useCallback(
    (event) => {
      if (!disabled && event.buttons === 1) {
        dataTableService.selectCell(coordinate, { append: event.shiftKey });
      }
    },
    [coordinate, dataTableService, disabled]
  );

  const handleMouseEnter: MouseEventHandler<HTMLElement> = useCallback(
    (event) => {
      if (!disabled && event.buttons === 1) {
        dataTableService.selectCell(coordinate, { append: true });
      }
    },
    [coordinate, dataTableService, disabled]
  );

  const handleMouseUp: MouseEventHandler<HTMLElement> = useCallback(
    (event) => {
      if (!disabled && event.buttons === 1) {
        dataTableService.selectCell(coordinate, { append: true });
      }
    },
    [coordinate, dataTableService, disabled]
  );

  const handleDoubleClick: MouseEventHandler<HTMLElement> = useCallback(
    (event) => {
      if (!disabled && _notNil(onDoubleClick)) {
        onDoubleClick();
      }
    },
    [disabled, onDoubleClick]
  );

  return {
    handleMouseDown,
    handleMouseEnter,
    handleMouseUp,
    handleDoubleClick,
  };
};
